<template>
  <div v-tooltip="avatarTooltipAttrs" :class="avatarClass" v-bind="$attrs">
    <slot>
      <div
        class="sb-avatar__image"
        :style="avatarStyle"
        :data-testid="`${dataTestid}-image`"
        v-show="isImageLoaded"
      >
        <img
          :src="src"
          :alt="friendlyName || name"
          @load="handleImageLoad"
          @error="handleImageError"
        />
      </div>
      <span
        v-if="!isImageLoaded && (name || friendlyName)"
        :class="['sb-avatar__initials', backgroundColorClass]"
        :data-testid="`${dataTestid}-initials`"
        :aria-label="name || friendlyName"
      >
        {{ initials }}
      </span>
      <svg
        v-else-if="!isImageLoaded && !name && !friendlyName"
        :class="['sb-avatar__fallback-icon', backgroundColorClass]"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" fill-rule="evenodd">
          <circle cx="12" cy="12" r="12" />
          <path
            fill="currentColor"
            d="M10.53 14.47c.537.536 1.024.78 1.47.78.446 0 .933-.244 1.47-.78a.75.75 0 0 1 1.06 1.06c-.796.797-1.642 1.22-2.53 1.22-.888 0-1.734-.423-2.53-1.22a.75.75 0 0 1 1.06-1.06zM9 9a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm6 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
          />
          <circle cx="17.5" cy="13.5" r="2.5" opacity=".091" />
          <circle cx="6.5" cy="13.5" r="2.5" opacity=".091" />
        </g>
      </svg>
    </slot>

    <div v-if="showTextContainer" class="sb-avatar__text-container">
      <span
        v-if="isDescriptionTop"
        :class="descriptionClass"
        :data-testid="`${dataTestid}-top-description`"
      >
        {{ description }}
      </span>
      <span class="sb-avatar__text" :data-testid="`${dataTestid}-text`">
        {{ friendlyName || name }}
      </span>
      <span
        v-if="isDescriptionBottom"
        :class="descriptionClass"
        :data-testid="`${dataTestid}-bottom-description`"
      >
        {{ description }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { includes } from '../../utils'
import type { PropType } from 'vue'

import {
  isSizeValid,
  getInitials,
  generateRandomBgColor,
  type AvatarBackgroundColor,
  type AvatarSize,
} from './utils'

import { Tooltip } from '../../directives'

const POSITION_TYPES = ['top', 'bottom']

export default {
  name: 'SbAvatar',

  directives: {
    tooltip: Tooltip,
  },

  props: {
    bgColor: {
      type: String as PropType<AvatarBackgroundColor>,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    descriptionPosition: {
      type: String as PropType<'top' | 'bottom'>,
      default: 'top',
      validator: (position) => includes(POSITION_TYPES, position),
    },
    name: {
      type: String,
      default: null,
    },
    friendlyName: {
      type: String,
      default: null,
    },
    size: {
      type: String as PropType<AvatarSize>,
      default: null,
      validator: isSizeValid,
    },
    showName: {
      type: Boolean,
      default: false,
    },
    src: {
      type: String,
      default: null,
    },
    borderColor: {
      type: String,
      default: null,
    },
    useTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipVariant: {
      type: String,
      default: 'dark',
    },
    tooltipFullLength: {
      type: Boolean,
      default: true,
    },
    tooltipPosition: {
      type: String,
      default: 'bottom',
    },
  },

  data() {
    return {
      isImageLoaded: false,
    }
  },

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-avatar'
    },

    avatarClass() {
      const avatarSizeClass =
        this.size && this.size !== 'normal' && `sb-avatar--${this.size}`
      return ['sb-avatar', avatarSizeClass]
    },

    avatarStyle() {
      const isColorValid = this.borderColor && this.borderColor.startsWith('#')

      return {
        borderColor: isColorValid ? this.borderColor : undefined,
        borderWidth: isColorValid ? '2px' : undefined,
      }
    },

    backgroundColorClass() {
      const color =
        this.bgColor || generateRandomBgColor(this.friendlyName || this.name)

      return `sb-avatar--bg-color-${color}`
    },

    descriptionClass() {
      return [
        'sb-avatar__description',
        `sb-avatar__description--${this.descriptionPosition}`,
      ]
    },

    avatarTooltipAttrs() {
      return (
        this.useTooltip && {
          label: this.friendlyName ? this.friendlyName : this.name,
          position: this.tooltipPosition,
          variant: this.tooltipVariant,
          isFullLength: this.tooltipFullLength,
        }
      )
    },

    avatarImageAttrs() {
      return this.useTooltip && { tabindex: 0 }
    },

    initials() {
      return getInitials(this.friendlyName || this.name)
    },

    showDescription() {
      return this.showName && this.description
    },

    showTextContainer() {
      return (
        this.showName && (this.name || this.friendlyName) && !this.useTooltip
      )
    },

    isDescriptionTop() {
      return this.showDescription && this.descriptionPosition === 'top'
    },

    isDescriptionBottom() {
      return this.showDescription && this.descriptionPosition === 'bottom'
    },
  },

  methods: {
    handleImageLoad() {
      this.isImageLoaded = true
    },

    handleImageError() {
      this.isImageLoaded = false
    },
  },
}
</script>
